<script setup lang="ts">
import { localeTag } from '@/i18n'
/**
 * Passpartout — "Cambia utente" a palette (stile ricerca globale):
 * input col focus immediato, risultati ricchi (azienda, ruoli, ULTIMO ACCESSO)
 * navigabili da tastiera, e sotto i CAMBI RECENTI di questa postazione
 * (equivalente moderno del cookie legacy `idpc`: /auth/passpartout/history
 * aggrega log_utenti per cookie staff, con fallback sull'IP).
 */
import { ref, watch, nextTick } from 'vue'
import Tag from 'primevue/tag'
import { apiClient } from '@/api/client'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

const visible = defineModel<boolean>('visible', { required: true })
const toast = useToast()
const { t } = useI18n()

interface PpUser {
  username: string
  nome: string | null
  cognome: string | null
  aziendaNome: string | null
  aziendaLocalita: string | null
  aziendaId: number | null
  ruoli: string | null
  ultimoAccesso: string | null
  /** Livello del ruolo più alto: serve a raggruppare, non a ordinare (ci pensa il server). */
  livelloRuolo: number | null
  /** false = account disabilitato o operatore non attivo/eliminato: arriva in fondo dal server. */
  attivo?: boolean
}

/**
 * Chi usa il passpartout cerca un PROFILO («un amministratore di quell'azienda»), non una
 * persona in ordine alfabetico: i risultati arrivano ordinati per livello di ruolo e qui si
 * raggruppano sotto un'intestazione, altrimenti l'ordine sembrerebbe arbitrario.
 */
function etichettaLivello(n: number): string {
  switch (n) {
    case 1: return t('layout.passpartout.livAmministratori', 'Amministratori')
    case 2: return t('layout.passpartout.livDirezione', 'Direzione')
    case 3: return t('layout.passpartout.livResponsabili', 'Responsabili')
    case 4: return t('layout.passpartout.livCoordinatori', 'Coordinatori')
    case 5: return t('layout.passpartout.livReferenti', 'Referenti')
    case 6: return t('layout.passpartout.livSegreteria', 'Segreteria')
    case 8: return t('layout.passpartout.livSenzaRuolo', 'Senza ruolo assegnato')
    default: return t('layout.passpartout.livAltri', 'Altri ruoli')
  }
}

/** Vero sulla prima riga di ogni gruppo: è lì che va l'intestazione. */
function primoDelGruppo(i: number): boolean {
  if (i === 0) return true
  return gruppo(risultati.value[i]) !== gruppo(risultati.value[i - 1])
}

/** I non attivi formano un gruppo a sé, in fondo, qualunque sia il loro ruolo. */
function nonAttivo(u?: PpUser): boolean {
  return u?.attivo === false
}

function gruppo(u?: PpUser): number {
  return nonAttivo(u) ? 99 : livello(u)
}

function livello(u?: PpUser): number {
  return u?.livelloRuolo ?? 8
}

function etichetta(u?: PpUser): string {
  return nonAttivo(u) ? t('layout.passpartout.livNonAttivi', 'Non attivi') : etichettaLivello(livello(u))
}

interface HistoryEntry {
  username: string
  nomeCompleto: string | null
  dataLogin: string | null
  address: string | null
}

const inputEl = ref<HTMLInputElement | null>(null)
const query = ref('')
const risultati = ref<PpUser[]>([])
const attivo = ref(0)
const searching = ref(false)
const switching = ref(false)
const recenti = ref<HistoryEntry[]>([])

let debounce: ReturnType<typeof setTimeout> | null = null

watch(visible, async (v) => {
  if (!v) return
  query.value = ''
  risultati.value = []
  attivo.value = 0
  await nextTick()
  inputEl.value?.focus()
  void caricaRecenti()
})

async function caricaRecenti(): Promise<void> {
  try {
    const { data } = await apiClient.get<{
      byCookie: HistoryEntry[]
      byIp: HistoryEntry[]
      generic: HistoryEntry[]
    }>('/auth/passpartout/history')
    // Postazione = cookie staff (come il legacy idpc); fallback: stesso IP
    recenti.value = (data.byCookie?.length ? data.byCookie : data.byIp) ?? []
  } catch {
    recenti.value = []
  }
}

function onInput(): void {
  if (debounce) clearTimeout(debounce)
  debounce = setTimeout(cerca, 250)
}

async function cerca(): Promise<void> {
  const q = query.value.trim()
  if (q.length < 2) {
    risultati.value = []
    return
  }
  searching.value = true
  try {
    const { data } = await apiClient.get<PpUser[]>('/auth/users/search', { params: { q } })
    risultati.value = data
    attivo.value = 0
  } catch {
    risultati.value = []
  } finally {
    searching.value = false
  }
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    attivo.value = Math.min(attivo.value + 1, risultati.value.length - 1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    attivo.value = Math.max(attivo.value - 1, 0)
  } else if (e.key === 'Enter') {
    const scelto = risultati.value[attivo.value]
    if (scelto) void loggaCome(scelto.username)
  }
}

async function loggaCome(username: string): Promise<void> {
  if (switching.value) return
  switching.value = true
  try {
    await apiClient.post('/auth/login-as', { username })
    // Reload completo per azzerare tutti gli store (menu, auth, dati pagina), restando sulla pagina in cui si era
    // (stesso indirizzo, filtri compresi): se il nuovo utente non può vederla, ci pensano i controlli di accesso della
    // rotta come per qualsiasi apertura diretta. Richiesta di Giuseppe Maltese, 22/10/2026.
    window.location.href = window.location.pathname + window.location.search + window.location.hash
  } catch {
    switching.value = false
    toast.add({ severity: 'error', summary: t('layout.passpartout.errore', 'Errore'), detail: t('layout.passpartout.cambioFallito', 'Cambio utente fallito'), life: 3000 })
  }
}

function fmtData(iso: string | null): string {
  if (!iso) return t('layout.passpartout.mai', 'mai')
  const d = new Date(iso)
  return d.toLocaleDateString(localeTag(), { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString(localeTag(), { hour: '2-digit', minute: '2-digit' })
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :show-header="false"
    dismissable-mask
    position="top"
    class="pp-palette"
    :style="{ width: '620px', maxWidth: '95vw', marginTop: '8vh' }"
  >
    <!-- Input di ricerca: focus immediato all'apertura -->
    <div class="pp-pal-search">
      <i class="pi pi-key pp-pal-key" />
      <input
        ref="inputEl"
        v-model="query"
        type="text"
        class="pp-pal-input"
        :placeholder="t('layout.passpartout.placeholder', 'Cambia utente — cerca nome, username o azienda…')"
        name="passpartout-cerca-utente"
        @input="onInput"
        @keydown="onKeydown"
      >
      <i
        v-if="searching || switching"
        class="pi pi-spin pi-spinner text-muted-color"
      />
    </div>

    <!-- Risultati -->
    <ul
      v-if="risultati.length"
      class="pp-pal-list"
    >
      <template
        v-for="(u, i) in risultati"
        :key="u.username"
      >
        <li
          v-if="primoDelGruppo(i)"
          class="pp-pal-gruppo"
        >
          {{ etichetta(u) }}
        </li>
        <li
          class="pp-pal-row"
          :class="{ 'pp-pal-row--attiva': i === attivo, 'pp-pal-row--spento': nonAttivo(u) }"
          @mouseenter="attivo = i"
          @click="loggaCome(u.username)"
        >
        <div class="pp-pal-row-main">
          <span class="pp-pal-nome">
            {{ [u.cognome, u.nome].filter(Boolean).join(' ') || u.username }}
            <span class="pp-pal-username">@{{ u.username }}</span>
            <Tag
              v-if="nonAttivo(u)"
              severity="danger"
              :value="t('layout.passpartout.nonAttivo', 'Non attivo')"
              class="pp-pal-badge"
            />
          </span>
          <span class="pp-pal-az">
            {{ u.aziendaNome }}<template v-if="u.aziendaId"> ({{ u.aziendaId }})</template>
            <template v-if="u.aziendaLocalita"> · {{ u.aziendaLocalita }}</template>
            <template v-if="u.ruoli"> · {{ u.ruoli }}</template>
          </span>
        </div>
        <span
            class="pp-pal-accesso"
            :title="t('layout.passpartout.ultimoAccesso', 'Ultimo accesso')"
          >
            <i class="pi pi-clock" /> {{ fmtData(u.ultimoAccesso) }}
          </span>
        </li>
      </template>
      <li
        v-if="risultati.length >= 40"
        class="pp-pal-gruppo pp-pal-gruppo--avviso"
      >
        {{ t('layout.passpartout.altriRisultati', 'Ci sono altri risultati oltre questi: restringi la ricerca.') }}
      </li>
    </ul>
    <p
      v-else-if="query.trim().length >= 2 && !searching"
      class="pp-pal-empty"
    >
      {{ t('layout.passpartout.nessunUtente', 'Nessun utente trovato.') }}
    </p>
    <p
      v-else
      class="pp-pal-empty"
    >
      {{ t('layout.passpartout.digita', 'Digita almeno 2 caratteri — ↑↓ per scegliere, Invio per entrare.') }}
    </p>

    <!-- Cambi recenti da questa postazione (cookie staff; fallback stesso IP) -->
    <div
      v-if="recenti.length"
      class="pp-pal-recenti"
    >
      <div class="pp-pal-recenti-title">
        <i class="pi pi-history" /> {{ t('layout.passpartout.recenti', 'Cambi recenti da questa postazione') }}
      </div>
      <ul class="pp-pal-list">
        <li
          v-for="r in recenti"
          :key="r.username + (r.dataLogin ?? '')"
          class="pp-pal-row pp-pal-row--recente"
          @click="loggaCome(r.username)"
        >
          <div class="pp-pal-row-main">
            <span class="pp-pal-nome">
              {{ r.nomeCompleto || r.username }}
              <span class="pp-pal-username">@{{ r.username }}</span>
            </span>
          </div>
          <span class="pp-pal-accesso">{{ fmtData(r.dataLogin) }}</span>
        </li>
      </ul>
    </div>
  </Dialog>
</template>

<style scoped>
.pp-pal-search {
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.35rem 0.2rem 0.6rem;
  border-bottom: 1px solid var(--p-content-border-color);
}
.pp-pal-key { color: #7c3aed; font-size: 1.1rem; }
.pp-pal-input {
  flex: 1; border: 0; outline: none; background: transparent;
  font-size: 1.05rem; color: var(--p-text-color); padding: 0.35rem 0;
}
.pp-pal-list { list-style: none; margin: 0.4rem 0 0; padding: 0; }
/* Intestazione di gruppo: dice perché l'ordine non è alfabetico, senza rubare spazio alle righe. */
.pp-pal-gruppo {
  padding: 0.45rem 0.75rem 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
}
.pp-pal-row--spento .pp-pal-nome, .pp-pal-row--spento .pp-pal-az { opacity: 0.6; }
.pp-pal-badge { margin-left: 0.4rem; font-size: 0.65rem; padding: 0.05rem 0.4rem; vertical-align: middle; }
.pp-pal-gruppo--avviso {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: var(--p-orange-600, #ea580c);
}
.pp-pal-row {
  display: flex; align-items: center; justify-content: space-between; gap: 0.8rem;
  padding: 0.5rem 0.6rem; border-radius: 8px; cursor: pointer;
}
.pp-pal-row--attiva, .pp-pal-row:hover { background: var(--p-content-hover-background); }
.pp-pal-row-main { display: flex; flex-direction: column; min-width: 0; }
.pp-pal-nome { font-weight: 600; }
.pp-pal-username { font-weight: 400; color: var(--p-text-muted-color); margin-left: 0.3rem; }
.pp-pal-az {
  font-size: 0.8rem; color: var(--p-text-muted-color);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pp-pal-accesso {
  font-size: 0.78rem; color: var(--p-text-muted-color); white-space: nowrap;
  display: inline-flex; align-items: center; gap: 0.3rem;
  font-variant-numeric: tabular-nums;
}
.pp-pal-empty { color: var(--p-text-muted-color); font-size: 0.85rem; margin: 0.8rem 0.2rem; }
.pp-pal-recenti { margin-top: 0.6rem; border-top: 1px solid var(--p-content-border-color); padding-top: 0.5rem; }
.pp-pal-recenti-title {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--p-text-muted-color); display: flex; align-items: center; gap: 0.4rem;
  margin: 0 0.2rem 0.3rem;
}
.pp-pal-row--recente .pp-pal-nome { font-weight: 500; }
</style>
